import { Injectable } from '@angular/core';
import { FileModel, MedResultItemVM } from '@ca/core/models';
import { UserService } from '@ca/core/services';
import { AppEnvironment } from '@ca/environment/app-environment.enum';
import { environment } from '@ca/environment/environment';
import {
    APEER_BLOB_STORAGE_URL_PART,
    FILE_PREVIEW_TYPES,
    FILE_TYPES_SUPPORTED_BY_ARIVIS,
    PERMISSION_VIEWER_INDUSTRY,
} from 'src/app/shared/constants/constants';
import * as UrlUtils from '../../../utils/url/url-utils';

@Injectable({
    providedIn: 'root',
})
export class ArivisService {
    constructor(private userService: UserService) {}

    get arivisViewerUrlPart(): string {
        return this.userService.hasPermissions([PERMISSION_VIEWER_INDUSTRY], false) ? '/viewer-industry/?' : '/viewer/?';
    }

    isSupportedByArivis(typeOrName?: string): boolean {
        if (typeOrName == null) {
            return false;
        }

        const typeOrNameInLowerCase = typeOrName.toLowerCase();

        return FILE_TYPES_SUPPORTED_BY_ARIVIS.some(x => typeOrNameInLowerCase.endsWith(x));
    }

    isSupportedByFilePreviewer(typeOrNameOrUrl?: string): boolean {
        if (typeOrNameOrUrl == null) {
            return false;
        }
        const typeOrNameOrUrlToTest = UrlUtils.removeQueryParameter(typeOrNameOrUrl);
        return FILE_PREVIEW_TYPES.some(x => typeOrNameOrUrlToTest.endsWith(x));
    }

    isFilePreviewSupportedByArivis(file?: FileModel): boolean {
        if (file == null || file.storageType == null) {
            return false;
        }

        const fileUrl = file.url || file.token || '';
        const isFileUrlContainingBlogStorageUrlPart = fileUrl.includes(APEER_BLOB_STORAGE_URL_PART);
        if (!isFileUrlContainingBlogStorageUrlPart && file.storageType === 'Web') {
            return false;
        }
        return this.isSupportedByArivis(file.type);
    }

    generateArivisMlViewerUrl(files: FileModel | (FileModel | MedResultItemVM)[], datasetId?: string, embeddedViewer?: boolean): string {
        let url = this.generateArivisViewerUrlWithImageId(files, embeddedViewer);
        if (datasetId != null) {
            url += '&datasetId=' + datasetId;
        }
        return url;
    }

    /**
     * Generates an URL to open Arivis viewer. Can open the viewer with one or several images.
     * Uses the file ID as parameter, not the file URL.
     */
    generateArivisViewerUrlWithImageId(files: FileModel | MedResultItemVM | (FileModel | MedResultItemVM)[], embeddedViewer: boolean = false): string {
        let viewerUrl = this.arivisViewerUrlPart;
        if (embeddedViewer) {
            viewerUrl += 'embedded&';
        }

        const fileIdString = this.getFileIdsFromOneOrSeveralFiles(files);
        return `${environment.arivisUrlPrefix}${viewerUrl}imageid=${fileIdString}`;
    }

    getFileIdsFromOneOrSeveralFiles(file: FileModel | MedResultItemVM | (FileModel | MedResultItemVM)[]): string {
        let fileIdString = '';
        if (Array.isArray(file)) {
            const ids: string[] = file.map((f: FileModel) => f.id);
            fileIdString = ids.join(',');
        } else {
            fileIdString = file.id;
        }

        return fileIdString;
    }

    /**
     * Generates an Arivis Viewer URL (with the url).
     *
     * @deprecated Don't use this method anymore. For new implementations, use generateArivisViewerUrlNew(), which takes the fileId as a
     * parameter (instead of the URL).
     */
    generateArivisViewerUrl(downloadUrl?: string, embeddedViewer: boolean = false): string {
        let viewerUrl = '';

        const debugBaseUrlEmbedded: string = 'http://' + environment.name + '/apeer/index.php/apeer/image/view?embedded&';
        const baseUrlEmbedded: string = environment.type !== AppEnvironment.Production ? debugBaseUrlEmbedded : '/viewer/?embedded&';
        const baseUrlStandalone: string = '/viewer/?';

        if (downloadUrl?.length) {
            let url: URL;
            try {
                url = new URL(downloadUrl);
            } catch (error) {
                // in case it is not valid url prevent blocking the UI and just return an empty string
                return '';
            }

            const account = url.hostname;
            const pathname = url.pathname;
            // we need to decode and encode pathname as the blob URL is already encoded from backend
            const blob = encodeURIComponent(decodeURIComponent(pathname));
            const sas = encodeURIComponent(url.search.substring(1)); // remove ? which is first character of search

            if (embeddedViewer) {
                viewerUrl = baseUrlEmbedded;
            } else {
                viewerUrl = baseUrlStandalone;
            }

            viewerUrl += 'account=' + account + '&blob=' + blob + '&sas=' + sas;
        }

        return environment.arivisUrlPrefix + viewerUrl;
    }
}
